import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/lib/lang";
import { getApp } from "@/lib/apps";
import { buildPrivacy } from "@/lib/privacy";
import { scrollToTarget } from "@/lib/lenis";
import { EASE, Eyebrow, MaskLines, Reveal } from "@/components/motion";
import NotFound from "@/pages/NotFound";

export default function Privacy() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const app = slug ? getApp(slug) : null;
  if (slug && !app) return <NotFound />;
  const sections = buildPrivacy(lang, app);
  const subject = app ? app.name : "ZenVio";

  return (
    <div data-testid={app ? `privacy-page-${app.slug}` : "privacy-page-studio"} className="relative pb-24 pt-32 sm:pt-40">
      <div className="container-x">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }}>
          <Link to={app ? `/apps/${app.slug}` : "/"} data-testid="privacy-back-link" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1" />
            {app ? app.name : "ZenVio"}
          </Link>
          <Eyebrow className="mt-10">{t.privacy.title}</Eyebrow>
        </motion.div>
        <h1 key={lang + subject} data-testid="privacy-title" className="mt-6 font-display text-5xl font-extrabold leading-[0.9] tracking-[-0.045em] sm:text-7xl lg:text-8xl">
          <MaskLines lines={[subject, t.privacy.title]} delay={0.1} lineClassName={(i) => (i === 1 ? "text-white/35" : "")} />
        </h1>
        <div className="mt-10 flex flex-wrap gap-x-10 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/45">
          <span data-testid="privacy-updated">{t.privacy.updated}: {lang === "da" ? "Juni 2026" : "June 2026"}</span>
          <span>{t.privacy.for}: {app ? `${app.name} (iOS & Android)` : t.privacy.website}</span>
        </div>

        <div className="mt-20 grid gap-16 lg:grid-cols-12">
          <aside className="self-start lg:sticky lg:top-32 lg:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{t.privacy.toc}</p>
            <ol className="mt-5 space-y-2.5 text-sm">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <button type="button" data-testid={`privacy-toc-${s.id}`} onClick={() => scrollToTarget(`#${s.id}`)} className="group flex gap-3 text-left text-white/55 transition-colors hover:text-white">
                    <span className="font-mono text-xs text-white/30">{String(i + 1).padStart(2, "0")}</span>
                    <span className="transition-transform duration-500 group-hover:translate-x-1">{s.h}</span>
                  </button>
                </li>
              ))}
            </ol>
          </aside>
          <article className="space-y-14 lg:col-span-8 lg:col-start-5">
            {sections.map((s, i) => (
              <Reveal key={s.id + lang} y={24} className="scroll-mt-32 border-t border-white/10 pt-8" id={s.id} data-testid={`privacy-section-${s.id}`}>
                <h2 className="flex items-baseline gap-4 font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  <span className="font-mono text-xs font-normal text-white/30">{String(i + 1).padStart(2, "0")}</span>{s.h}
                </h2>
                {s.p?.map((p, j) => <p key={j} className="mt-4 leading-relaxed text-white/65">{p}</p>)}
                {s.list && (
                  <ul className="mt-5 space-y-3">
                    {s.list.map((li, j) => (
                      <li key={j} className="flex gap-3 leading-relaxed text-white/70">
                        <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-r from-[#22d3ee] to-[#5cf2a0]" />{li}
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            ))}
          </article>
        </div>
      </div>
    </div>
  );
}
